import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { getApiBaseUrl } from '../utils/api';
import { 
  MagnifyingGlass, 
  User, 
  EnvelopeSimple, 
  Phone, 
  PencilSimple, 
  Key,
  Archive,
  ArrowCounterClockwise,
  Users,
  Calendar,
  CheckCircle,
  Warning,
  Eye,
  X
} from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

export function AdminParentsManagement() {
  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentDetails, setParentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadParents();
  }, [searchQuery, showArchived]);

  const loadParents = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (showArchived) params.append('include_archived', 'true');
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/parents?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setParents(data);
      }
    } catch (error) {
      console.error('Load parents error:', error);
      toast.error('Failed to load parent accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadParentDetails = async (parentId) => {
    setDetailsLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/parents/${parentId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setParentDetails(data);
      }
    } catch (error) {
      console.error('Load parent details error:', error);
      toast.error('Failed to load parent details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = async (parent) => {
    setSelectedParent(parent);
    setShowDetailsDialog(true);
    await loadParentDetails(parent.user_id);
  };

  const handleEditClick = (parent) => {
    setSelectedParent(parent);
    setEditForm({
      name: parent.name || '',
      email: parent.email || '',
      phone: parent.phone || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/parents/${selectedParent.user_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(editForm)
        }
      );
      
      if (response.ok) {
        toast.success('Account updated successfully');
        setShowEditDialog(false);
        loadParents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update account');
      }
    } catch (error) {
      toast.error('Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/parents/${selectedParent.user_id}/reset-password`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowResetDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleArchive = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/parents/${selectedParent.user_id}/archive`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowArchiveDialog(false);
        loadParents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update account status');
      }
    } catch (error) {
      toast.error('Failed to update account status');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-parents-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <h1 className="text-xl font-heading">PARENT ACCOUNTS</h1>
          <p className="text-sm text-muted-foreground">
            Manage parent/guardian accounts
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Search & Filters */}
        <Card className="bg-card border-border">
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-parents-input"
                />
              </div>
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => setShowArchived(!showArchived)}
                className="whitespace-nowrap"
                data-testid="toggle-archived-btn"
              >
                <Archive size={18} className="mr-2" />
                {showArchived ? 'Showing All' : 'Show Archived'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-primary">{parents.filter(p => !p.archived).length}</div>
              <p className="text-xs text-muted-foreground">Active Accounts</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-muted-foreground">{parents.filter(p => p.archived).length}</div>
              <p className="text-xs text-muted-foreground">Archived</p>
            </CardContent>
          </Card>
        </div>

        {/* Parents List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">
              {parents.length} {parents.length === 1 ? 'ACCOUNT' : 'ACCOUNTS'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {parents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <User size={48} className="mx-auto mb-4 opacity-50" />
                <p>No parent accounts found</p>
              </div>
            ) : (
              parents.map((parent) => (
                <div
                  key={parent.user_id}
                  className={`p-4 rounded-lg border ${parent.archived ? 'bg-muted/10 border-muted/30' : 'bg-muted/20 border-border'}`}
                  data-testid={`parent-card-${parent.user_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-medium truncate">{parent.name}</h3>
                        {parent.archived && (
                          <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-500 border-yellow-500/30">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                        <EnvelopeSimple size={14} className="flex-shrink-0" />
                        <span className="truncate">{parent.email}</span>
                      </p>
                      {parent.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone size={14} className="flex-shrink-0" />
                          {parent.phone}
                        </p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Users size={14} />
                          {parent.player_count || 0} players
                        </span>
                        {parent.credit_balance > 0 && (
                          <span className="text-green-500">
                            ${parent.credit_balance.toFixed(2)} credit
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 sm:gap-2 justify-end flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10"
                        onClick={() => handleViewDetails(parent)}
                        data-testid={`view-details-btn-${parent.user_id}`}
                      >
                        <Eye size={16} className="sm:hidden" />
                        <Eye size={18} className="hidden sm:block" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10"
                        onClick={() => handleEditClick(parent)}
                        data-testid={`edit-btn-${parent.user_id}`}
                      >
                        <PencilSimple size={16} className="sm:hidden" />
                        <PencilSimple size={18} className="hidden sm:block" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10"
                        onClick={() => {
                          setSelectedParent(parent);
                          setShowResetDialog(true);
                        }}
                        data-testid={`reset-btn-${parent.user_id}`}
                      >
                        <Key size={16} className="sm:hidden" />
                        <Key size={18} className="hidden sm:block" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 sm:h-10 sm:w-10 ${parent.archived ? 'text-green-500' : 'text-yellow-500'}`}
                        onClick={() => {
                          setSelectedParent(parent);
                          setShowArchiveDialog(true);
                        }}
                        data-testid={`archive-btn-${parent.user_id}`}
                      >
                        {parent.archived ? (
                          <>
                            <ArrowCounterClockwise size={16} className="sm:hidden" />
                            <ArrowCounterClockwise size={18} className="hidden sm:block" />
                          </>
                        ) : (
                          <>
                            <Archive size={16} className="sm:hidden" />
                            <Archive size={18} className="hidden sm:block" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>
              Update account information for {selectedParent?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1"
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email Address</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1"
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-1"
                data-testid="edit-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Send a password reset email to {selectedParent?.email}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will send an email with a link to reset their password. The link will expire in 24 hours.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={saving} data-testid="confirm-reset-btn">
              {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <Key size={18} className="mr-2" />}
              Send Reset Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedParent?.archived ? 'Restore Account' : 'Archive Account'}
            </DialogTitle>
            <DialogDescription>
              {selectedParent?.archived 
                ? `Restore access for ${selectedParent?.name}?`
                : `Archive the account for ${selectedParent?.name}?`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedParent?.archived ? (
              <p className="text-sm text-muted-foreground">
                This will restore the account and allow the user to log in again.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Archiving will:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Prevent the user from logging in</li>
                  <li>Keep all records for future reference</li>
                  <li>Allow you to restore the account later</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleToggleArchive} 
              disabled={saving}
              variant={selectedParent?.archived ? "default" : "destructive"}
              data-testid="confirm-archive-btn"
            >
              {saving ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              {selectedParent?.archived ? 'Restore Account' : 'Archive Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User size={24} />
              {selectedParent?.name}
            </DialogTitle>
            <DialogDescription>
              Account details and history
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="py-8 text-center">
              <LoadingSpinner size="lg" className="mx-auto" />
            </div>
          ) : parentDetails ? (
            <div className="space-y-6 py-4">
              {/* Account Info */}
              <div>
                <h4 className="font-medium mb-2 text-sm text-muted-foreground">ACCOUNT INFO</h4>
                <div className="bg-muted/20 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple size={16} className="text-muted-foreground" />
                    <span>{parentDetails.parent.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-muted-foreground" />
                    <span>{parentDetails.parent.phone || 'Not provided'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-muted-foreground" />
                    <span>Joined {formatDate(parentDetails.parent.created_at)}</span>
                  </div>
                  {parentDetails.credit_balance > 0 && (
                    <div className="flex items-center gap-2 text-green-500">
                      <span className="font-medium">Credit Balance: ${parentDetails.credit_balance.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Players */}
              <div>
                <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                  PLAYERS ({parentDetails.players.length})
                </h4>
                {parentDetails.players.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No players registered</p>
                ) : (
                  <div className="space-y-2">
                    {parentDetails.players.map((player) => (
                      <div key={player.player_id} className="bg-muted/20 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{player.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Age {player.age} • {player.age >= 12 ? '12-14' : '9-11'} group
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {player.waiver_status === 'signed' ? (
                            <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                              <CheckCircle size={14} className="mr-1" /> Waiver Signed
                            </Badge>
                          ) : player.waiver_status === 'expired' ? (
                            <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
                              <Warning size={14} className="mr-1" /> Waiver Expired
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                              <X size={14} className="mr-1" /> No Waiver
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Registrations */}
              <div>
                <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                  RECENT REGISTRATIONS ({parentDetails.registrations.length})
                </h4>
                {parentDetails.registrations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No registrations yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {parentDetails.registrations.map((reg) => (
                      <div key={reg.registration_id} className="bg-muted/20 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{reg.player?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {reg.session?.date} at {reg.session?.start_time}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {reg.location?.name}
                            </p>
                          </div>
                          <Badge className={
                            reg.payment_status === 'paid' 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-yellow-500/20 text-yellow-500'
                          }>
                            {reg.payment_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileNavigation userRole="admin" />
    </div>
  );
}
